import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, Star, ArrowDown } from "lucide-react";
import { MaskLine } from "./Reveal";
import { PROFILE, IMAGES } from "../lib/data";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.62, 0.9]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);

  return (
    <section ref={ref} id="top" className="relative h-[100svh] min-h-[680px] overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.hero} alt="High Court colonnade" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-[#0A0A0A]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-[#0A0A0A]/70" />

      <motion.div style={{ y: textY }} className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-16 lg:pb-24">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          className="mb-6 flex items-center gap-3"
        >
          <span className="overline">{PROFILE.court}</span>
          <span className="h-px w-10 bg-[#C8A24B]/60" />
          <span className="flex items-center gap-1.5 text-[#C8A24B]">
            {[...Array(5)].map((_, i) => (
              <Star key={i} size={13} fill="#C8A24B" strokeWidth={0} />
            ))}
            <span className="font-mono text-xs text-[#F7F5F2]/80 ml-1">{PROFILE.rating} · {PROFILE.reviews} reviews</span>
          </span>
        </motion.div>

        <h1 className="font-serif font-light leading-[0.92] tracking-tight text-[#F7F5F2] text-[15vw] sm:text-[11vw] lg:text-[8.5vw]">
          <MaskLine delay={0.35}>Manpreet Singh</MaskLine>
          <MaskLine delay={0.5} className="italic text-[#C8A24B]">Bhatti</MaskLine>
        </h1>

        <div className="mt-6 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <MaskLine delay={0.7} className="max-w-xl text-[#8C8C8C] text-base sm:text-lg leading-relaxed font-light">
            Advocate — Criminal Litigation. Bail, quashing, appeals, NDPS &amp; criminal family matters, argued with dedication before the Punjab &amp; Haryana High Court.
          </MaskLine>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8 }}
            className="flex flex-wrap items-center gap-3"
          >
            <a
              href={`tel:${PROFILE.phoneTel}`}
              data-testid="hero-call-button"
              className="inline-flex items-center gap-2 rounded-sm bg-[#F7F5F2] text-[#0A0A0A] px-6 py-3.5 text-sm font-medium hover:bg-[#C8A24B] transition-colors duration-300"
            >
              <Phone size={16} strokeWidth={1.75} /> {PROFILE.phoneDisplay}
            </a>
            <a
              href={PROFILE.whatsapp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-sm border border-[#F7F5F2]/25 text-[#F7F5F2] px-6 py-3.5 text-sm font-medium hover:border-[#C8A24B] hover:text-[#C8A24B] transition-colors duration-300"
            >
              <MessageCircle size={16} strokeWidth={1.75} /> WhatsApp
            </a>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 hidden sm:flex items-center gap-2 text-[#8C8C8C]"
      >
        <span className="font-mono text-[0.65rem] uppercase tracking-[0.25em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
          <ArrowDown size={14} />
        </motion.span>
      </motion.div>
    </section>
  );
};
